import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { z } from 'zod';

import { functions } from '../firebase.js';
import { SchemaValidationFailedError, StaffRoleSchema } from '@kilat/shared';

/**
 * Typed wrapper around the `inviteStaff` Cloud Function (Sprint 55).
 *
 * Server validates the same Zod shape; this client-side parse catches
 * obvious bugs before the round-trip.
 */

export const InviteStaffInputSchema = z.object({
  tenantId: z.string().min(1),
  outletId: z.string().min(1),
  phone: z.string().min(8).max(20),
  role: StaffRoleSchema,
  displayName: z.string().min(1).max(80).optional(),
});
export type InviteStaffInput = z.infer<typeof InviteStaffInputSchema>;

export const InviteStaffOutputSchema = z.object({
  uid: z.string(),
  userCreated: z.boolean(),
  role: StaffRoleSchema,
});
export type InviteStaffOutput = z.infer<typeof InviteStaffOutputSchema>;

const inviteStaffFn: HttpsCallable<InviteStaffInput, InviteStaffOutput> = httpsCallable(
  functions,
  'inviteStaff',
);

export async function callInviteStaff(input: InviteStaffInput): Promise<InviteStaffOutput> {
  const parsed = InviteStaffInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('inviteStaff.input', parsed.error.flatten());
  }
  const result = await inviteStaffFn(parsed.data);
  const out = InviteStaffOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('inviteStaff.output', out.error.flatten());
  }
  return out.data;
}
